/**
 * Interaction Invocation Log — Wave 22.13
 *
 * Append-only ring-buffer that records UI invocation events
 * (modal opens/closes, deferred actions, event bus traffic)
 * for the DevTools Plugin Interaction Inspector.
 *
 * Kept as a standalone module so all subsystems can import it
 * without circular dependencies.
 */

import type { UIInvocationLogEntry, UIInvocationKind } from '../types/plugin-interaction';

const MAX_LOG_SIZE = 500;

let _nextLogId = 1;

function generateLogId(): string {
  return `log-${Date.now()}-${_nextLogId++}`;
}

class InvocationLog {
  private _entries: UIInvocationLogEntry[] = [];
  private _listeners: Set<() => void> = new Set();

  /** Append a new log entry, evicting oldest if over capacity. */
  append(partial: Omit<UIInvocationLogEntry, 'id' | 'timestamp'>): UIInvocationLogEntry {
    const entry: UIInvocationLogEntry = {
      id: generateLogId(),
      timestamp: new Date().toISOString(),
      ...partial,
    };

    this._entries.push(entry);

    if (this._entries.length > MAX_LOG_SIZE) {
      this._entries = this._entries.slice(-MAX_LOG_SIZE);
    }

    this._notify();
    return entry;
  }

  /** Get all entries (newest last). */
  getAll(): UIInvocationLogEntry[] {
    return [...this._entries];
  }

  /** Get the last N entries. */
  getRecent(n: number): UIInvocationLogEntry[] {
    return this._entries.slice(-n);
  }

  /** Filter by kind. */
  getByKind(kind: UIInvocationKind): UIInvocationLogEntry[] {
    return this._entries.filter((e) => e.kind === kind);
  }

  /** Filter by plugin. */
  getByPlugin(pluginId: string): UIInvocationLogEntry[] {
    return this._entries.filter((e) => e.pluginId === pluginId);
  }

  /** Clear the log. */
  clear(): void {
    this._entries = [];
    this._notify();
  }

  /** Subscribe to changes. Returns an unsubscribe function. */
  subscribe(listener: () => void): () => void {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }

  get size(): number {
    return this._entries.length;
  }

  private _notify(): void {
    for (const listener of this._listeners) {
      try {
        listener();
      } catch {
        // DevTools listener errors must not crash the host.
      }
    }
  }
}

/** Singleton invocation log shared across all interaction subsystems. */
export const invocationLog = new InvocationLog();
